import React, { useEffect, useState } from "react";
import { Table, Button, Typography, Switch, message, Select, Space, Modal } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import {
  fetchWaitingList,
  saveUserStatusChanges,
  deleteUserFromWaitingList, // Import the delete API function
} from "../useAPIDetails/usewaitingList";

const { Option } = Select;
const { Title } = Typography;
const { confirm } = Modal; // Destructure confirm from Modal

const WaitingList = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filterStatus, setFilterStatus] = useState("NOT_VALIDATED");
  const [statusChanges, setStatusChanges] = useState([]); // Track user changes

  // Fetch data from API
  const loadData = async (page, size, status) => {
    setLoading(true);
    try {
      const result = await fetchWaitingList(page - 1, size, status);
      setData(
        result.data.users?.map((user) => ({
          key: user?.id,
          ...user,
          status:
            user?.status === "NOT_VALIDATED" ? "Not Validated" : user?.status,
        }))
      );
      setPagination({
        current: page,
        pageSize: size,
        total: result.data.pagination.totalElements,
      });
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to load waiting list."
      );
    } finally {
      setLoading(false);
    }
  };

  // Load initial data
  useEffect(() => {
    loadData(pagination.current, pagination.pageSize, filterStatus);
  }, [filterStatus]); // dont add pagination here it created infinity loop

  // Handle table pagination
  const handleTableChange = (pagination) => {
    loadData(pagination.current, pagination.pageSize, filterStatus);
  };

  // Handle toggle change (update statusChanges array)
  const handleToggleChange = (id, status) => {
    setStatusChanges((prevChanges) => {
      const existingChange = prevChanges.find((change) => change.id === id);

      if (!status) {
        // Remove entry if status is null (toggled off)
        return prevChanges.filter((change) => change.id !== id);
      }

      if (existingChange) {
        // Update existing entry
        return prevChanges?.map((change) =>
          change.id === id ? { id, status } : change
        );
      }
      return [...prevChanges, { id, status }];
    });
  };

  // Handle save changes
  const handleSaveChanges = async () => {
    if (statusChanges.length === 0) {
      message.info("No changes to save.");
      return;
    }
    try {
      await saveUserStatusChanges(statusChanges); // Replace with actual API call
      message.success("Changes saved successfully.");
      setStatusChanges([]); // Clear local changes
      loadData(pagination.current, pagination.pageSize); // Refresh data
    } catch (error) {
      message.error(
        error?.response?.data?.message || "Failed to save changes."
      );
    }
  };

  // Handle delete user with confirmation dialog
  const handleDelete = (id) => {
    confirm({
      title: "Are you sure you want to delete this user?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        try {
          await deleteUserFromWaitingList(id); // Call the delete API
          message.success("User deleted successfully.");
          loadData(pagination.current, pagination.pageSize, filterStatus); // Refresh the list
        } catch (error) {
          message.error(
            error?.response?.data?.message || "Failed to delete user."
          );
        }
      },
      onCancel() {
        message.info("Delete action canceled.");
      },
    });
  };

  // Table columns
  const columns = [
    {
      title: "User Name",
      dataIndex: "userName",
      key: "userName",
      align: "center",
    },
    {
      title: "Email Address",
      dataIndex: "email",
      key: "email",
      align: "center",
    },
    {
      title: "Mobile Number",
      dataIndex: "phoneNumber",
      key: "phoneNumber",
      align: "center",
      render: (value) => (value ? value : "N/A"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => (status ? status : "N/A"),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (text, record) => {
        const isInStatus = statusChanges.find(
          (change) => change.id === record.id && change.status === "IN"
        );
        const isOutStatus = statusChanges.find(
          (change) => change.id === record.id && change.status === "OUT"
        );

        return (
          <div
            style={{ display: "flex", gap: "16px", justifyContent: "center" }}
          >
            <Switch
              checked={!!isInStatus}
              onChange={(checked) =>
                handleToggleChange(record.id, checked ? "IN" : null)
              }
              checkedChildren="IN"
              unCheckedChildren="IN"
            />
            <Switch
              checked={!!isOutStatus}
              onChange={(checked) =>
                handleToggleChange(record.id, checked ? "OUT" : null)
              }
              checkedChildren="OUT"
              unCheckedChildren="OUT"
            />
            <Button
              danger
              type="link"
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)} // Show confirmation dialog on click
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="flex-grow-content" style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          Waiting List
        </Title>
        <div style={{ display: "flex", gap: "10px" }}>
          <Select
            defaultValue="NOT_VALIDATED"
            style={{ width: 120 }}
            onChange={(value) => setFilterStatus(value)}
          >
            <Option value="NOT_VALIDATED">Not validated</Option>
            <Option value="IN">IN</Option>
            <Option value="OUT">OUT</Option>
          </Select>
        </div>
      </div>
      <Table
        columns={columns}
        dataSource={data}
        loading={loading}
        rowKey={(record) => record.key}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
        }}
        onChange={handleTableChange}
        style={{
          border: "1px solid #E1E1E1",
          borderRadius: "5px",
        }}
      />
      <div style={{ marginTop: "16px", textAlign: "right" }}>
        <Button
          type="primary"
          style={{
            backgroundColor:
              statusChanges?.length === 0 ? "#F0F0F0" : "#FF7D4D",
            color: "#fff",
            border: "none",
            borderRadius: "5px",
          }}
          onClick={handleSaveChanges}
          disabled={statusChanges?.length === 0}
        >
          Save Changes
        </Button>
      </div>
    </div>
  );
};

export default WaitingList;